import React from 'react';
import { PaginationData, PaginationParams } from '@hero/hero-types';
import getQueryParams from '@hero/hero-utils/getQueryParams';
import useGetDeviceLogFolders from '../api/useGetDeviceLogFolders';
import Button from '@hero/ui-kit/inputs/Button';
import usePrepareDeviceLogs from '../api/usePrepareDeviceLogs';
import QueryInlineError from '../../../../components/QueryInlineError';
import Table from '@hero/ui-kit/tables/Table';
import getTableColumns, { LogsTableItem } from './table/getTableColumns';
import usePagerProps from '../../../../hooks/usePagerProps';
import useGetDeviceLogFolderFiles from '../api/useGetDeviceLogFolderFiles';
import Container from '@hero/ui-kit/layout/Container';
import CircularButton from '@hero/ui-kit/inputs/CircularButton';
import DownloadIcon from '@hero/ui-kit/icons/utility/DownloadIcon';
import downloadFile from '../../../../utils/downloadFile';
import P from '@hero/ui-kit/typography/P';
import InlineLoader from '../../../../components/InlineLoader';

const rowStyle: React.CSSProperties = {
    padding: '4px',
    textAlign: 'left'
};

const FileItem: React.FC<{
    name: string;
    url: string;
    onSelectChange: (name: string) => void;
    selected?: boolean;
}> = ({ name, url, onSelectChange, selected }) => {
    const handleDownloadButton = () => {
        downloadFile(url, name);
    };

    const handleChange = () => {
        onSelectChange(name);
    };

    return (
        <tr>
            <td style={rowStyle}>
                <input checked={selected} type="checkbox" onChange={handleChange} />
            </td>
            <td style={rowStyle}>
                <P noDefaultMargin size="large">
                    {name}
                </P>
            </td>
            <td style={rowStyle}>
                <CircularButton variant="secondary" isLink={false} onClick={handleDownloadButton}>
                    <DownloadIcon size="utility" id={name} />
                </CircularButton>
            </td>
        </tr>
    );
};

const DeviceLogsInner: React.FC<{
    folders: string[];
    onUploadLogs: () => void;
    isFetchingFolders?: boolean;
    pagerProps: ReturnType<typeof usePagerProps>;
    deviceId: number;
}> = ({ folders, onUploadLogs, isFetchingFolders = false, pagerProps, deviceId }) => {
    const [selectedFolder, setSelectedFolder] = React.useState<string | undefined>();

    const [selectedFiles, setSelectedFiles] = React.useState<string[]>([]);

    const { data: filesResponse, isFetching: isDeviceLogFoldersLoading } =
        useGetDeviceLogFolderFiles(
            { device_id: deviceId, folder: selectedFolder },
            !!selectedFolder
        );

    const files = filesResponse?.data.files_and_urls || {};

    const countFiles = Object.keys(files).length;

    const handleSelectFiles = (name: string) => {
        const index = selectedFiles.indexOf(name);
        if (index === -1) {
            setSelectedFiles([...selectedFiles, name]);
        } else {
            setSelectedFiles(selectedFiles.filter((file) => file !== name));
        }
    };

    const handleSelectFolderAction = (folder: string) => {
        setSelectedFolder(folder);
    };

    const handleSelectAllFiles = () => {
        selectedFiles.length !== countFiles
            ? setSelectedFiles(Object.keys(files))
            : setSelectedFiles([]);
    };

    const tableColumns = React.useMemo(
        () => getTableColumns(handleSelectFolderAction),
        [handleSelectFolderAction]
    );

    const handleDownloadAllFiles = () => {
        selectedFiles.forEach((name) => {
            downloadFile(files[name], name);
        });
    };

    const logs = folders.map((folder) => ({ folder, files: folder }));

    return (
        <div>
            <Button size="small" onClick={onUploadLogs}>
                Upload logs
            </Button>
            <Container gridTemplateColumns={'1fr 1fr'} styles={{ marginTop: '1.2rem' }}>
                {pagerProps.total_count ? (
                    <div style={{ maxWidth: '60rem' }}>
                        <Table<LogsTableItem>
                            columns={tableColumns}
                            data={logs}
                            rowKey="folder"
                            emptyValues="-"
                            isLoading={isFetchingFolders}
                            pagerProps={pagerProps}
                        />
                    </div>
                ) : null}
                <div>
                    {isDeviceLogFoldersLoading ? (
                        <InlineLoader />
                    ) : countFiles ? (
                        <>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    columnGap: '2rem',
                                    marginBottom: '1.2rem'
                                }}
                            >
                                <Button
                                    size="small"
                                    onClick={handleDownloadAllFiles}
                                    disabled={selectedFiles.length === 0}
                                >
                                    Download selected
                                </Button>
                                <h3>Folder: {selectedFolder} files</h3>
                            </div>

                            <table
                                style={{
                                    borderCollapse: 'collapse',
                                    maxWidth: '60rem',
                                    minWidth: '48rem'
                                }}
                            >
                                <tr>
                                    <th
                                        style={{
                                            ...rowStyle,
                                            display: 'flex',
                                            alignItems: 'center',
                                            columnGap: '1.2rem'
                                        }}
                                    >
                                        <input
                                            checked={selectedFiles.length === countFiles}
                                            type="checkbox"
                                            onChange={handleSelectAllFiles}
                                        />
                                        <p>Select</p>
                                    </th>
                                    <th style={rowStyle}>Name</th>
                                    <th style={rowStyle}>Download</th>
                                </tr>
                                {Object.entries(files).map(([name, url]) => (
                                    <FileItem
                                        key={name}
                                        name={name}
                                        url={url}
                                        onSelectChange={handleSelectFiles}
                                        selected={selectedFiles.includes(name)}
                                    />
                                ))}
                            </table>
                        </>
                    ) : null}
                </div>
            </Container>
        </div>
    );
};

const DeviceLogs: React.FC<{ deviceId: number }> = ({ deviceId }) => {
    const { offset = 0, limit = 10 } = getQueryParams<PaginationParams>();

    const {
        data: foldersResponse,
        error,
        isError,
        isFetching: isFetchingFolders,
        refetch
    } = useGetDeviceLogFolders({ device_id: deviceId, offset, limit });

    const { mutateAsync, error: prepareLogsError } = usePrepareDeviceLogs();

    const handlePrepareUploadLogs = () => {
        mutateAsync({ device_id: deviceId })
            .then(() => {
                refetch();
            })
            .catch(() => {});
    };

    const errorsData = error?.response?.data as undefined | { errors?: string[] };
    const folders = foldersResponse?.data.folders || [];
    const paginationData: PaginationData = {
        total_count: foldersResponse?.data.total_count || 0,
        offset,
        limit
    };
    const pagerProps = usePagerProps(paginationData);

    const isNotSupported = errorsData?.errors && errorsData.errors[0] === 'not_supported';

    const supported = !isError || (!isNotSupported && !isFetchingFolders);

    return supported ? (
        <div>
            <DeviceLogsInner
                isFetchingFolders={isFetchingFolders}
                onUploadLogs={handlePrepareUploadLogs}
                folders={folders}
                pagerProps={pagerProps}
                deviceId={deviceId}
            />
            {prepareLogsError ? <QueryInlineError error={prepareLogsError} /> : null}
        </div>
    ) : null;
};

export default DeviceLogs;
